
import { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { GameProvider } from './contexts/GameContext';
import { PresenceProvider } from './contexts/PresenceContext';
import { ToastProvider } from './contexts/ToastContext';
import { AnalyticsProvider } from './components/AnalyticsProvider';
import { EmulatorStatus } from './components/ui/EmulatorStatus';
import { useRoomReconnection } from './hooks/useRoomReconnection';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home').then(module => ({ default: module.Home })));
const GameModeSelection = lazy(() => import('./pages/GameModeSelection').then(module => ({ default: module.GameModeSelection })));
const ConfigureGame = lazy(() => import('./pages/ConfigureGame').then(module => ({ default: module.ConfigureGame })));
const Lobby = lazy(() => import('./pages/Lobby').then(module => ({ default: module.Lobby })));
const Game = lazy(() => import('./pages/Game').then(module => ({ default: module.Game })));
const Results = lazy(() => import('./pages/Results').then(module => ({ default: module.Results })));
const Summary = lazy(() => import('./pages/Summary').then(module => ({ default: module.Summary })));
const About = lazy(() => import('./pages/About').then(module => ({ default: module.About })));
const Privacy = lazy(() => import('./pages/Privacy').then(module => ({ default: module.Privacy })));
const YoNuncaOnline = lazy(() => import('./pages/YoNuncaOnline').then(module => ({ default: module.YoNuncaOnline })));
const JuegoParaBeber = lazy(() => import('./pages/JuegoParaBeber').then(module => ({ default: module.JuegoParaBeber })));
const RetosPrevia = lazy(() => import('./pages/RetosPrevia').then(module => ({ default: module.RetosPrevia })));
const HowToPlay = lazy(() => import('./pages/HowToPlay').then(module => ({ default: module.HowToPlay })));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen bg-gradient-to-br from-purple-900 to-blue-900 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-white/20 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-white/80 text-lg">Cargando...</p>
    </div>
  </div>
);

function App() {
  return (
    <AnalyticsProvider>
      <AuthProvider>
        <ToastProvider>
          <GameProvider>
            <PresenceProvider>
              <Router>
                <AppContent />
              </Router>
            </PresenceProvider>
          </GameProvider>
        </ToastProvider>
      </AuthProvider>
    </AnalyticsProvider>
  );
}

// Separate component to use router hooks inside Router context
function AppContent() {
  // Initialize room reconnection hook (now inside Router context)
  useRoomReconnection();
  
  // Debug log to confirm components are loading
  // console.log('🎨 AppContent: Component loaded at', new Date().toLocaleTimeString());
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-blue-900">
      {/* Show emulator status in development */}
      {import.meta.env.VITE_USE_EMULATORS === 'true' && <EmulatorStatus />}
      
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Home page */}
          <Route path="/" element={<Home />} />
          
          {/* Game mode selection */}
          <Route path="/seleccionar-modo" element={<GameModeSelection />} />
          
          {/* Configure games */}
          <Route path="/configurar" element={<ConfigureGame />} />
          <Route path="/configurar-desafios" element={<ConfigureGame gameMode="challenges" />} />
          <Route path="/configurar-impostor" element={<ConfigureGame gameMode="impostor" />} />

          {/* Game flow routes */}
          <Route path="/lobby/:code" element={<Lobby />} />
          <Route path="/game/:code" element={<Game />} />
          <Route path="/results/:code" element={<Results />} />
          <Route path="/summary/:code" element={<Summary />} />
          
          {/* Content pages for SEO and AdSense */}
          <Route path="/acerca" element={<About />} />
          <Route path="/privacidad" element={<Privacy />} />
          <Route path="/como-jugar" element={<HowToPlay />} />

          {/* SEO Landing Pages */}
          <Route path="/yo-nunca-online" element={<YoNuncaOnline />} />
          <Route path="/juego-para-beber" element={<JuegoParaBeber />} />
          <Route path="/retos-previa" element={<RetosPrevia />} />
          
          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;